// frontend/src/components/AdminTeamDetail/AddPlayerModal.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { adminService } from '../../services/supabaseClient';
import FlagDisplay from '../FlagDisplay';
import { getCountryCode } from '../../utils/countryFlags';

const AddPlayerModal = ({
    show,
    onClose,
    onAddManualPlayer,
    onConnectPlayer,
    eventCode,
    initialQuery = ''  // New prop for auto-populating search
}) => {
    const [activeTab, setActiveTab] = useState('search');
    const [sportradarQuery, setSportradarQuery] = useState('');
    const [sportradarResults, setSportradarResults] = useState([]);
    const [sportradarPageOffset, setSportradarPageOffset] = useState(0);
    const [hasMoreResults, setHasMoreResults] = useState(false);
    const [isSearchingSportradar, setIsSearchingSportradar] = useState(false);
    const [newPlayerName, setNewPlayerName] = useState('');
    const [filterNationality, setFilterNationality] = useState('');
    const [filterGender, setFilterGender] = useState('');
    const [filterBirthYear, setFilterBirthYear] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const resultsContainerRef = useRef(null);

    // Generate search variations from a full name (passport-style)
    // e.g., "Camila Victoria Escudero Barra" -> ["Camila Escudero", "Victoria Barra", "Camila Barra", etc.]
    const generateNameVariations = (fullName) => {
        if (!fullName) return [fullName];
        const parts = fullName.trim().split(/\s+/);
        if (parts.length <= 2) return [fullName]; // Already simple name

        const variations = new Set([fullName]); // Include original

        // First + Last
        if (parts.length >= 2) {
            variations.add(`${parts[0]} ${parts[parts.length - 1]}`);
        }

        // All two-word combinations
        for (let i = 0; i < parts.length; i++) {
            for (let j = i + 1; j < parts.length; j++) {
                variations.add(`${parts[i]} ${parts[j]}`);
            }
        }

        return Array.from(variations);
    };

    // Debounce Search with smart variations
    useEffect(() => {
        const timer = setTimeout(async () => {
            if (activeTab === 'search' && show && sportradarQuery.length >= 3) {
                setIsSearchingSportradar(true);
                setSportradarPageOffset(0);

                // Generate name variations for smarter search
                const variations = generateNameVariations(sportradarQuery);

                // Search all variations in parallel
                const searchPromises = variations.slice(0, 5).map(query =>
                    adminService.searchSportradar(query, 0)
                );

                const results = await Promise.all(searchPromises);

                // Merge and deduplicate results
                const allResults = results.flatMap(r => r.data || []);
                const uniqueResults = [];
                const seenIds = new Set();
                for (const person of allResults) {
                    if (!seenIds.has(person.personId)) {
                        seenIds.add(person.personId);
                        uniqueResults.push(person);
                    }
                }

                setSportradarResults(uniqueResults);
                setHasMoreResults(uniqueResults.length >= 50);
                setIsSearchingSportradar(false);
            } else if (sportradarQuery.length < 3) {
                setSportradarResults([]);
                setHasMoreResults(false);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [sportradarQuery, activeTab, show]);

    // Set initial query when modal opens
    useEffect(() => {
        if (show && initialQuery) {
            setSportradarQuery(initialQuery);
            setActiveTab('search');
        }
    }, [show, initialQuery]);

    // Reset state when modal closes
    useEffect(() => {
        if (!show) {
            setSportradarQuery('');
            setSportradarResults([]);
            setNewPlayerName('');
            setFilterNationality('');
            setFilterGender('');
            setFilterBirthYear('');
            setHighlightedIndex(-1);
        }
    }, [show]);

    // Reset highlight when results change
    useEffect(() => {
        setHighlightedIndex(-1);
    }, [sportradarResults, filterNationality, filterGender, filterBirthYear]);

    // Filtered results — must be declared before handleKeyDown
    const filteredResults = sportradarResults.filter(person => {
        if (filterNationality && person.nationality !== filterNationality) return false;
        if (filterGender && person.gender !== filterGender) return false;
        if (filterBirthYear) {
            if (!person.dob) return false;
            const year = person.dob.split('-')[0];
            if (year !== filterBirthYear) return false;
        }
        return true;
    });

    // Keyboard navigation handler
    const handleKeyDown = useCallback((e) => {
        if (!show) return;

        if (e.key === 'Escape') {
            onClose();
            return;
        }

        if (activeTab !== 'search' || filteredResults.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex(prev => {
                const next = Math.min(prev + 1, filteredResults.length - 1);
                // Scroll highlighted row into view
                const rows = resultsContainerRef.current?.querySelectorAll('tbody tr');
                if (rows && rows[next]) rows[next].scrollIntoView({ block: 'nearest' });
                return next;
            });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex(prev => {
                const next = Math.max(prev - 1, 0);
                const rows = resultsContainerRef.current?.querySelectorAll('tbody tr');
                if (rows && rows[next]) rows[next].scrollIntoView({ block: 'nearest' });
                return next;
            });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlightedIndex >= 0 && highlightedIndex < filteredResults.length) {
                onConnectPlayer(filteredResults[highlightedIndex]);
            }
        }
    }, [show, activeTab, filteredResults, highlightedIndex, onClose, onConnectPlayer]);

    // Attach keyboard listener
    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    const handleLoadMore = async () => {
        const newOffset = sportradarPageOffset + 50;
        setIsSearchingSportradar(true);
        const { data } = await adminService.searchSportradar(sportradarQuery, newOffset);
        if (data && data.length > 0) {
            setSportradarResults(prev => [...prev, ...data]);
            setSportradarPageOffset(newOffset);
            setHasMoreResults(data.length >= 50);
        } else {
            setHasMoreResults(false);
        }
        setIsSearchingSportradar(false);
    };

    const handleAddManualPlayer = () => {
        if (newPlayerName.trim()) {
            onAddManualPlayer(newPlayerName.trim());
            setNewPlayerName('');
            onClose();
        }
    };


    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b">
                    <h3 className="text-lg font-semibold">Add Player to Roster</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b">
                    <button
                        className={`flex-1 py-3 text-sm font-medium border-b-2 ${activeTab === 'search' ? 'border-mjwc-primary text-mjwc-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        onClick={() => setActiveTab('search')}
                    >
                        Search Sportradar
                    </button>
                    <button
                        className={`flex-1 py-3 text-sm font-medium border-b-2 ${activeTab === 'manual' ? 'border-mjwc-primary text-mjwc-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        onClick={() => setActiveTab('manual')}
                    >
                        Manual Entry
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto flex-1">
                    {activeTab === 'search' ? (
                        <div className="space-y-4">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search by name (min 3 chars)..."
                                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-mjwc-primary focus:border-transparent"
                                    value={sportradarQuery}
                                    onChange={(e) => setSportradarQuery(e.target.value)}
                                    autoFocus
                                />
                                <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>

                            {/* Filters Row */}
                            {sportradarResults.length > 0 && (
                                <div className="flex gap-4 mt-3">
                                    <select
                                        value={filterNationality}
                                        onChange={(e) => setFilterNationality(e.target.value)}
                                        className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-mjwc-primary focus:border-transparent"
                                    >
                                        <option value="">All Nationalities</option>
                                        {[...new Set(sportradarResults.map(p => p.nationality).filter(Boolean))].sort().map(nat => (
                                            <option key={nat} value={nat}>{nat}</option>
                                        ))}
                                    </select>
                                    <select
                                        value={filterGender}
                                        onChange={(e) => setFilterGender(e.target.value)}
                                        className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-mjwc-primary focus:border-transparent"
                                    >
                                        <option value="">All Genders</option>
                                        <option value="MALE">Male</option>
                                        <option value="FEMALE">Female</option>
                                    </select>
                                    <select
                                        value={filterBirthYear}
                                        onChange={(e) => setFilterBirthYear(e.target.value)}
                                        className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-mjwc-primary focus:border-transparent"
                                    >
                                        <option value="">All Birth Years</option>
                                        {[...new Set(sportradarResults
                                            .map(p => p.dob ? p.dob.split('-')[0] : null)
                                            .filter(Boolean)
                                        )].sort().reverse().map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </select>
                                    {(filterNationality || filterGender || filterBirthYear) && (
                                        <button
                                            onClick={() => { setFilterNationality(''); setFilterGender(''); setFilterBirthYear(''); }}
                                            className="px-3 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg flex items-center gap-1 transition-colors"
                                            title="Clear all filters"
                                        >
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                            Clear
                                        </button>
                                    )}
                                </div>
                            )}

                            <div className="min-h-[300px]" ref={resultsContainerRef}>
                                {isSearchingSportradar && sportradarResults.length === 0 ? (
                                    <div className="flex justify-center py-12">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mjwc-primary"></div>
                                    </div>
                                ) : sportradarResults.length > 0 ? (
                                    <>
                                        <table className="w-full">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-2 w-14"></th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nationality</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Gender</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">DOB</th>
                                                    <th className="px-4 py-2 text-right">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {filteredResults.map((person, index) => (
                                                    <tr
                                                        key={person.personId}
                                                        className={`cursor-pointer transition-colors ${
                                                            highlightedIndex === index
                                                                ? 'bg-indigo-50 ring-1 ring-inset ring-indigo-200'
                                                                : 'hover:bg-gray-50'
                                                        }`}
                                                        onMouseEnter={() => setHighlightedIndex(index)}
                                                        onClick={() => onConnectPlayer(person)}
                                                    >
                                                        <td className="px-4 py-3 w-14">
                                                            {person.images && person.images.length > 0 ? (
                                                                <img
                                                                    src={`${person.images[0].url}?size=200&format=webp`}
                                                                    alt=""
                                                                    className="w-10 h-10 rounded-full object-cover"
                                                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                                                />
                                                            ) : null}
                                                            <div
                                                                className="w-10 h-10 rounded-full bg-gray-200 items-center justify-center text-gray-500 text-sm font-medium"
                                                                style={{ display: person.images && person.images.length > 0 ? 'none' : 'flex' }}
                                                            >
                                                                {(person.nameFullLatin || person.nameFullLocal || '?').charAt(0).toUpperCase()}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <div className="font-medium text-gray-900">{person.nameFullLatin || person.nameFullLocal}</div>
                                                            {person.nameFullLocal && person.nameFullLocal !== person.nameFullLatin && (
                                                                <div className="text-xs text-gray-500">{person.nameFullLocal}</div>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm">
                                                            {person.nationality ? (
                                                                <div className="flex items-center gap-2">
                                                                    {getCountryCode(person.nationality) && (
                                                                        <div className="w-6 h-4 flex items-center justify-center overflow-hidden rounded-sm border border-gray-200 shrink-0">
                                                                            <FlagDisplay teamName={person.nationality} size="sm" />
                                                                        </div>
                                                                    )}
                                                                    <span>{person.nationality}</span>
                                                                </div>
                                                            ) : '-'}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm">{person.gender}</td>
                                                        <td className="px-4 py-3 text-sm">{person.dob}</td>
                                                        <td className="px-4 py-3 text-right">
                                                            <button
                                                                onClick={() => onConnectPlayer(person)}
                                                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                                                                title="Add/Link"
                                                            >
                                                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                                </svg>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {hasMoreResults && (
                                            <div className="p-4 text-center">
                                                <button
                                                    onClick={handleLoadMore}
                                                    disabled={isSearchingSportradar}
                                                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium disabled:opacity-50"
                                                >
                                                    {isSearchingSportradar ? 'Loading...' : 'Load More'}
                                                </button>
                                            </div>
                                        )}
                                    </>
                                ) : sportradarQuery.length >= 3 ? (
                                    <div className="text-center py-12 text-gray-500">No results found</div>
                                ) : (
                                    <div className="text-center py-12 text-gray-400">Type to search...</div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="max-w-md mx-auto">
                            <h3 className="text-lg font-semibold mb-4">Manual Entry</h3>
                            <input
                                type="text"
                                placeholder="Player name"
                                value={newPlayerName}
                                onChange={(e) => setNewPlayerName(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mjwc-primary mb-4"
                                autoFocus
                            />
                            <div className="flex gap-2">
                                <button
                                    onClick={onClose}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddManualPlayer}
                                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                                >
                                    Add Player
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddPlayerModal;
